<script setup lang="ts">
import { onMounted } from "vue";
import { trackTab } from "@/analytics/drawing-analytics";
import DrawerMenuContent from "./DrawerMenuContent.vue";
import { useLayersList } from "@/composable/useLayersList";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

const {
  layers,
  updateLayers,
  toggleLayerVisibility,
  toggleLayerLock,
  lockNonLegend,
  unlockAll,
} = useLayersList();

onMounted(() => updateLayers());

function onToggleLayerVisibility(layer: Parameters<typeof toggleLayerVisibility>[0]) {
  trackTab("layers", "layer_visibility_toggled");
  toggleLayerVisibility(layer);
}

function onToggleLayerLock(layer: Parameters<typeof toggleLayerLock>[0]) {
  trackTab("layers", "layer_lock_toggled");
  toggleLayerLock(layer);
}

function onLockNonLegend() {
  trackTab("layers", "non_legend_locked");
  lockNonLegend();
}

function onUnlockAll() {
  trackTab("layers", "all_unlocked");
  unlockAll();
}
</script>

<template>
  <DrawerMenuContent
    :label="$t('canvas:settings.layers.section.title')"
    :description="$t('canvas:settings.layers.section.description')"
  >
    <div class="d-flex gap-2 mb-3">
      <button class="mini-btn" @click="onLockNonLegend()">
        <PhosphorIcon name="lock" :size="14" /> Verrouiller hors légende
      </button>
      <button class="mini-btn" @click="onUnlockAll()">Tout déverrouiller</button>
    </div>

    <div class="list-group" ref="list">
      <div
        v-for="(layer, id) in layers"
        :key="id"
        class="d-flex align-items-center rounded gap-2 py-1"
        :class="[layer.visibility ? 'visible-layer' : 'not-visible-layer']"
      >
        <button
          class="toggle-button rounded"
          @click="() => onToggleLayerVisibility(layer)"
        >
          <PhosphorIcon
            :name="layer.visibility ? 'eye' : 'eye-slash'"
            :size="22"
          />
        </button>
        <div class="canvas-divider-vertical"></div>

        <span
          class="layer-color-swatch"
          :style="{ backgroundColor: layer.colorCss }"
        ></span>

        <div class="col text-truncate">
          {{ layer.name }}
        </div>

        <button
          class="toggle-button rounded"
          :title="layer.isLocked ? 'Déverrouiller' : 'Verrouiller'"
          @click="() => onToggleLayerLock(layer)"
        >
          <PhosphorIcon
            :name="layer.isLocked ? 'lock' : 'lock-open'"
            :size="18"
            :style="{ opacity: layer.isLocked ? 1 : 0.4 }"
          />
        </button>
      </div>
    </div>
  </DrawerMenuContent>
</template>

<style type="scss" scoped>
.visible-layer {
  color: white;
}

.not-visible-layer {
  color: white;
  opacity: 0.5;
}

.toggle-button {
  background-color: transparent;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 1.5rem;
  &:hover {
    background-color: white;
    color: black;
  }
}

.mini-btn {
  background-color: transparent;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.25rem;
  font-size: 0.75rem;
  padding: 2px 8px;
  cursor: pointer;
  &:hover {
    background-color: rgba(255, 255, 255, 0.12);
  }
}

.layer-color-swatch {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: inline-block;
  flex-shrink: 0;
}
</style>
